/**
 * /check-in?clientId=X — F-015 Hero Tracker daily check-in (2026-05-22).
 *
 * Replaces the previous AM/PM bookend tabs with two sections on one page:
 *   1. Daily KPIs — Energy / Work / Love sliders (1..10). One entry per day.
 *   2. Hero Tracker — three groups (AM bookend · PM bookend · Anywhere).
 *      Items are added by tools / step transitions / the client / the coach.
 *      Per-item type is yes_no (toggle, default false) or number (stepper,
 *      default 0). Client can reorder within a group via up/down arrows
 *      and add new items via the +Add item form at the bottom of each group.
 *
 * Save button: F-pristine pattern. Enabled (flame) when current values
 * differ from the last saved baseline, disabled (muted) when pristine.
 * On successful save: brief "Saved ✓" pulse → settle to disabled.
 * Re-enables on the next change.
 *
 * Persists:
 *   - POST /api/v1/clients/:id/daily-tracking (KPIs only: energy/work/love)
 *   - POST /api/v1/clients/:id/tracking-values (batch upsert today's values)
 *
 * Empty state: a brand-new client with no items added yet sees the KPI
 * section + a single "Your Hero Tracker is empty yet — add an item below
 * or wait for your coach to set things up." line in place of the three
 * group cards.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { usePristine } from '@/lib/use-pristine';
import { ConfidenceLogCard } from '@/components/confidence-log-card';
import { WeeklyFocusCard } from '@/components/weekly-focus-card';
import { WeeklyReviewBanner } from '@/components/weekly-review-banner';
import { PrintButton } from '@/components/print-button';
import { AxisIcon, AxisChip } from '@/components/axis-icon';
import { StepEntryBanner } from '@/components/step-entry-banner';
import { WinCelebration } from '@/components/win-celebration';
import { OneThingCelebration } from '@/components/one-thing-celebration';

type Category = 'am_bookend' | 'pm_bookend' | 'anywhere';
type ItemType = 'yes_no' | 'number';

interface Kpis {
  energy: number;
  work: number;
  love: number;
}

const DEFAULT_KPIS: Kpis = { energy: 7, work: 7, love: 7 };
// #14 — render order is AM bookend → Anytime → PM bookend (the day's arc).
const CATEGORIES: readonly Category[] = ['am_bookend', 'anywhere', 'pm_bookend'] as const;

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultValueFor(type: ItemType): string {
  return type === 'yes_no' ? 'false' : '0';
}

export function CheckInPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const clientId = useClientId();

  // Coach / head_coach don't have a personal check-in — short-circuit.
  if (user && user.role !== 'client') {
    return (
      <section className="max-w-3xl mx-auto px-s5 py-s7">
        <header className="mb-s5">
          <h1 className="font-display text-display-lg text-ink">
            {t('checkIn.title')}
          </h1>
        </header>
        <article
          className="rounded-lg border border-bone-3 bg-bone-2 p-s6"
          data-testid="check-in-coach-only"
        >
          <p className="text-ink">{t('checkIn.coachFallback.body')}</p>
          <Link
            to="/clients"
            className="btn-primary mt-s4 inline-block"
            data-testid="check-in-coach-cta"
          >
            {t('checkIn.coachFallback.cta')}
          </Link>
        </article>
      </section>
    );
  }

  // IMPORTANT: do NOT call `useTranslation()` inline inside JSX below.
  // The fallback branch only renders while clientId is falsy (auth still
  // loading); the moment auth resolves and clientId populates, this
  // function returns `<CheckInForClient />` instead — a different hook
  // count for the same component instance, which trips React error #310
  // ("Rendered more hooks than during the previous render"). Use the
  // single `t` already destructured at the top of the component.
  return clientId ? <CheckInForClient clientId={clientId} accessToken={accessToken ?? ''} /> : (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="check-in-missing-client">
      <h1 className="font-display text-display-lg text-ink">{t('checkIn.title')}</h1>
      <p className="mt-s3 text-flame-ember">{t('checkIn.errors.missingClient')}</p>
    </section>
  );
}

/* ─────────────────────────────  Loaded form  ───────────────────────────── */

function shiftIso(iso: string, deltaDays: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function CheckInForClient({
  clientId,
  accessToken,
}: {
  clientId: string;
  accessToken: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  // F-016 (2026-05-22): the client can navigate backward up to 5 days
  // from today. Forward is capped at today (no pre-filling future days
  // — honest tracking is a same-day-or-after act).
  const today = todayISO();
  const baseEarliest = shiftIso(today, -5);
  const [searchParams] = useSearchParams();
  // #12 — honor ?date=YYYY-MM-DD on entry (the dashboard's Fulfillment bars
  // deep-link to a specific day); otherwise default to today.
  const dateParam = searchParams.get('date');
  const [date, setDate] = useState<string>(
    dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : today,
  );
  // #11 — the client cannot navigate to days before coaching started /
  // the tracker was enabled. Floor = coaching_start_date (or, absent that,
  // program_start_date), but never looser than the rolling 5-day window.
  const [coachingStartDate, setCoachingStartDate] = useState<string | null>(null);
  const earliest =
    coachingStartDate && coachingStartDate > baseEarliest ? coachingStartDate : baseEarliest;
  const canGoBack = date > earliest;
  const canGoForward = date < today;
  const isToday = date === today;

  // State
  const [items, setItems] = useState<api.TrackingItem[]>([]);
  const [kpis, setKpis] = useState<Kpis>(DEFAULT_KPIS);
  // Per-item values keyed by tracking_item_id — strings, interpreted per type.
  const [values, setValues] = useState<Record<string, string>>({});
  /**
   * NL-033 — client's "My #1 thing to get up today" free-text. Locks
   * the rest of the tracker UI at Step 6+ until non-empty.
   * Empty string = not entered; null = legacy pre-Step-6 row.
   */
  const [dailyWin, setDailyWin] = useState<string>('');
  /**
   * NL-033 — snapshot of the saved WIN. Used as the "loaded baseline"
   * for the WIN gate (the gate unlocks once `dailyWin` has at least 3
   * chars OR the saved value matches and is non-empty). The tracker
   * stays interactive once the WIN is saved — only the initial empty
   * state blocks interaction.
   */
  const [savedDailyWin, setSavedDailyWin] = useState<string>('');
  /**
   * NL-034 — toggles the WIN celebration banner. Flipped to true on a
   * successful save when the WIN transitions from empty → non-empty.
   * The banner self-dismisses + flips this back to false.
   */
  const [celebrateWin, setCelebrateWin] = useState(false);
  /**
   * batch #4 (#8) — ONE Thing daily declaration + done. Activated at Step 4
   * (the bookend step). Persists to `daily_tracking.one_thing_declaration`
   * (the textarea) + `one_thing_done` (the check-off) via its own save,
   * independent of the main check-in Save (mirrors the Confidence Log).
   * `savedOneThing` is the persisted declaration; `oneThingText` is the draft.
   */
  const [oneThingText, setOneThingText] = useState<string>('');
  const [savedOneThing, setSavedOneThing] = useState<string>('');
  const [oneThingDone, setOneThingDone] = useState<boolean>(false);
  const [oneThingSaving, setOneThingSaving] = useState(false);
  const [oneThingSavedPulse, setOneThingSavedPulse] = useState(false);
  const [oneThingError, setOneThingError] = useState<string | null>(null);
  // Lets the client re-open the declaration to revise a saved ONE Thing.
  const [editingOneThing, setEditingOneThing] = useState(false);
  // Flips true the moment the ONE Thing is checked off → fires the celebration.
  const [celebrateOneThing, setCelebrateOneThing] = useState(false);
  const dismissOneThingCelebration = useCallback(() => setCelebrateOneThing(false), []);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // NL-011 — the Confidence Log card gates on the client's current step
  // (>= 3 reveals it). Fetched once on mount; absent the result, the
  // card stays hidden (treated as < 3).
  const [currentStep, setCurrentStep] = useState<number>(0);
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const self = await api.getClientSelf(accessToken, clientId);
        if (active) {
          setCurrentStep(self.current_step);
          // #11 — floor for backward date navigation. Normalise to a bare
          // YYYY-MM-DD so the string comparison against `date` is sound
          // whether the server sends a date or a full datetime.
          const startRaw = self.coaching_start_date ?? self.program_start_date ?? null;
          setCoachingStartDate(startRaw ? startRaw.slice(0, 10) : null);
        }
      } catch {
        if (active) setCurrentStep(0);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  // #12 — confirm dialog shown when the client saves with all three KPI
  // sliders still at the untouched default (see kpisAreUntouchedDefault).
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  // #3 (2026-06-15) — confirm before removing a tracker item. The ✕ no
  // longer archives instantly; it opens this dialog. Confirming archives
  // (soft — coach still sees it greyed) and drops it from the check-in view.
  const [archiveConfirm, setArchiveConfirm] = useState<{
    id: string;
    label: string;
  } | null>(null);
  // F-032 — per-KPI saved baseline. `null` = no daily_tracking row for
  // this day (or that KPI is null on the row) → slider renders BLACK
  // (unset). After a successful save we update to the current values
  // → slider renders GREEN (saved). Dragging away → ORANGE (dirty).
  const [savedBaselines, setSavedBaselines] = useState<{
    energy: number | null;
    work: number | null;
    love: number | null;
  }>({ energy: null, work: null, love: null });
  // F-031 — curated virtue catalog so the client can tag tracker items
  // with E/W/L axes (chips backed by the coach's catalog). Empty until
  // the coach has built the Carpe Diem protocol.
  const [catalog, setCatalog] = useState<api.CarpeDiemCatalogVirtue[]>([]);

  // Dirty tracking: combined snapshot of kpis + values + WIN.
  // When the user reorders / adds / archives an item we also want the save
  // button to enable, because reorder + archive write through their own
  // dedicated endpoints (so technically the form itself isn't "dirty" in
  // that case), but a fresh check-in still has new values to save.
  const formSnapshot = useMemo(
    () => ({ kpis, values, dailyWin }),
    [kpis, values, dailyWin],
  );
  const { isPristine, markSaved } = usePristine<typeof formSnapshot>(formSnapshot);

  /**
   * NL-033 — Step 6+ tracker gate. The rest of the tracker (KPIs already
   * captured above, behaviour rows below) goes non-interactive until the
   * client has either typed a fresh WIN OR loaded a saved WIN.
   * Pre-Step-6 clients see the field as a Should (visible from Step 6
   * onward) — they never hit the gate.
   *
   * IMP-014 — raised from 3 to 10 chars so the WIN is a stated intention,
   * not a keyboard mash. Deliberately still gentle: ~two short words pass.
   */
  const WIN_GATE_MIN = 10;
  const winGateActive = currentStep >= 6;
  const trackerUnlocked =
    !winGateActive ||
    savedDailyWin.trim().length >= WIN_GATE_MIN ||
    dailyWin.trim().length >= WIN_GATE_MIN;

  // Hydration — re-runs whenever `date` changes. Fetches three things
  // for the selected day: the items catalogue (rarely changes day-to-
  // day but cheap), today's KPI row from daily_tracking, and the
  // tracker values. The form state + the pristine baseline are reset
  // to the loaded data so the save button only re-enables when the
  // user actually edits THIS DAY's values.
  useEffect(() => {
    let active = true;
    setHydrated(false);
    setSubmitError(null);
    void (async () => {
      try {
        const [itemsRes, valuesRes, dailyRes, catalogRes] = await Promise.all([
          api.listTrackingItems(accessToken, clientId),
          api.getTrackingValues(accessToken, clientId, { date }),
          api.listDailyTracking(accessToken, clientId, { date }),
          // F-031 — best-effort. A 404 or empty catalog just means the
          // coach hasn't built the Carpe Diem yet; the tracker still
          // renders without the axis picker.
          api.getCarpeDiemCatalog(accessToken, clientId).catch(() => []),
        ]);
        if (!active) return;
        setItems(itemsRes);
        setCatalog(
          catalogRes.filter((v) => v.status === 'active'),
        );
        // KPIs — load this day's row if it exists, else fall back to
        // the default. The kpi inputs are 1..10 and the schema allows
        // null, so coerce nulls back to the default for the slider UI.
        const dailyRow = dailyRes.data[0] ?? null;
        const loadedKpis: Kpis = {
          energy: dailyRow?.energy_kpi ?? DEFAULT_KPIS.energy,
          work: dailyRow?.work_kpi ?? DEFAULT_KPIS.work,
          love: dailyRow?.love_kpi ?? DEFAULT_KPIS.love,
        };
        setKpis(loadedKpis);
        // F-032 — per-KPI saved baseline. Drives the tri-state colour
        // (null=black/unset, !=current=orange/dirty, ==current=green/saved).
        setSavedBaselines({
          energy: dailyRow?.energy_kpi ?? null,
          work: dailyRow?.work_kpi ?? null,
          love: dailyRow?.love_kpi ?? null,
        });
        // NL-033 — pre-fill the WIN from the saved row + remember the
        // baseline so the gate stays unlocked across reloads.
        const loadedWin = dailyRow?.daily_win_text ?? '';
        setDailyWin(loadedWin);
        setSavedDailyWin(loadedWin);
        // batch #4 #8 — ONE Thing declaration + done for this day.
        const loadedOneThing = dailyRow?.one_thing_declaration ?? '';
        setOneThingText(loadedOneThing);
        setSavedOneThing(loadedOneThing);
        setOneThingDone(dailyRow?.one_thing_done ?? false);
        setEditingOneThing(false);
        setOneThingError(null);
        setCelebrateOneThing(false);
        // Tracker values — default to each item's defaultValue, then
        // overlay any rows that actually exist for this date.
        const valueMap: Record<string, string> = {};
        for (const item of itemsRes) {
          valueMap[item.id] = item.defaultValue;
        }
        for (const v of valuesRes) {
          valueMap[v.trackingItemId] = v.value;
        }
        setValues(valueMap);
        // Pristine baseline = exactly what was just loaded. The save
        // button stays disabled until the user changes a slider or
        // toggles an item from THIS DAY's loaded state.
        markSaved({ kpis: loadedKpis, values: valueMap, dailyWin: loadedWin });
      } catch {
        /* fall through — empty form */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, date, markSaved]);

  // Pulse "Saved ✓" for ~2.5s after a successful save then disable.
  const pulseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (pulseTimeout.current) clearTimeout(pulseTimeout.current);
  }, []);

  const dirty = !isPristine(formSnapshot);
  const canSubmit = hydrated && !submitting && dirty;

  // #12 — true when the client is about to persist KPIs they never set:
  // all three sliders at the default AND no KPI saved for this day yet.
  // Persisting 7/7/7 silently reads as a real score, so we confirm first.
  const kpisAreUntouchedDefault =
    kpis.energy === DEFAULT_KPIS.energy &&
    kpis.work === DEFAULT_KPIS.work &&
    kpis.love === DEFAULT_KPIS.love &&
    savedBaselines.energy === null &&
    savedBaselines.work === null &&
    savedBaselines.love === null;

  const doSave = async (includeKpis: boolean): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // 1. Daily KPIs (energy/work/love only — ritual columns are now legacy).
      // Use the api-client helper so the base URL gets prefixed correctly
      // (`api.sleepercising.com` on prod, not the www origin). When the
      // client chose to skip the untouched defaults (#12), write null so
      // the day's KPIs stay genuinely unset (sliders render black on reload).
      await api.submitDailyTracking(accessToken, clientId, {
        date,
        energy_kpi: includeKpis ? kpis.energy : null,
        work_kpi: includeKpis ? kpis.work : null,
        love_kpi: includeKpis ? kpis.love : null,
        // NL-033 — persist the WIN. Empty string when the field is blank
        // (server stores '' verbatim; null also acceptable but we send
        // the literal so coach edits behave predictably).
        daily_win_text: dailyWin.trim(),
      });
      // 2. Hero Tracker values (one batch).
      const batch: api.TrackingValue[] = items
        .filter((item) => !item.archivedAt)
        .map((item) => ({
          trackingItemId: item.id,
          date,
          value: values[item.id] ?? item.defaultValue,
        }));
      if (batch.length > 0) {
        await api.upsertTrackingValues(accessToken, clientId, batch);
      }
      markSaved(formSnapshot);
      // F-032 — promote the just-saved KPI values to the new baselines so
      // the sliders flip from orange (dirty) to green (saved). When KPIs
      // were skipped (#12), keep them null so they stay black (unset).
      setSavedBaselines(
        includeKpis
          ? { energy: kpis.energy, work: kpis.work, love: kpis.love }
          : { energy: null, work: null, love: null },
      );
      // NL-033 + NL-034 — promote the just-saved WIN; if it just
      // transitioned from empty → non-empty AND the gate is active
      // (Step 6+), trigger the celebration banner once.
      const newWin = dailyWin.trim();
      const wasEmpty = savedDailyWin.trim().length === 0;
      setSavedDailyWin(newWin);
      if (winGateActive && wasEmpty && newWin.length >= WIN_GATE_MIN) {
        setCelebrateWin(true);
      }
      setJustSaved(true);
      if (pulseTimeout.current) clearTimeout(pulseTimeout.current);
      pulseTimeout.current = setTimeout(() => setJustSaved(false), 2500);
    } catch (cause) {
      setSubmitError(
        cause instanceof ApiError
          ? cause.envelope.message
          : (cause as Error).message || t('checkIn.errors.saveFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Save-button entry point. Intercepts the untouched-default-KPI case
  // (#12) with a confirm dialog; otherwise saves straight through.
  const handleSave = (): void => {
    if (!canSubmit) return;
    if (kpisAreUntouchedDefault) {
      setKpiConfirmOpen(true);
      return;
    }
    void doSave(true);
  };

  // batch #4 #8 — ONE Thing. Activated at Step 4. `declared` = a persisted
  // declaration exists → the card moves up to the checkable banner.
  const oneThingGateActive = currentStep >= 4;
  const oneThingDeclared = savedOneThing.trim().length > 0;
  const oneThingDirty = oneThingText.trim() !== savedOneThing.trim();

  const saveOneThing = async (): Promise<void> => {
    const text = oneThingText.trim();
    if (text.length === 0 || text === savedOneThing.trim()) return;
    setOneThingSaving(true);
    setOneThingError(null);
    try {
      await api.submitDailyTracking(accessToken, clientId, {
        date,
        one_thing_declaration: text,
      });
      setSavedOneThing(text);
      setEditingOneThing(false);
      setOneThingSavedPulse(true);
      window.setTimeout(() => setOneThingSavedPulse(false), 2000);
    } catch (cause) {
      setOneThingError(
        cause instanceof ApiError ? cause.envelope.message : t('checkIn.oneThing.error'),
      );
    } finally {
      setOneThingSaving(false);
    }
  };

  const toggleOneThingDone = async (next: boolean): Promise<void> => {
    setOneThingDone(next); // optimistic
    setOneThingError(null);
    try {
      await api.submitDailyTracking(accessToken, clientId, { date, one_thing_done: next });
      if (next) setCelebrateOneThing(true);
    } catch (cause) {
      setOneThingDone(!next); // revert
      setOneThingError(
        cause instanceof ApiError ? cause.envelope.message : t('checkIn.oneThing.error'),
      );
    }
  };

  // Hero Tracker mutations
  const onAddItem = async (input: {
    label: string;
    category: Category;
    itemType: ItemType;
  }): Promise<void> => {
    const item = await api.createTrackingItem(accessToken, clientId, {
      label: input.label,
      category: input.category,
      itemType: input.itemType,
      defaultValue: defaultValueFor(input.itemType),
    });
    setItems((prev) => [...prev, item]);
    setValues((prev) => ({ ...prev, [item.id]: item.defaultValue }));
  };

  const onValueChange = (itemId: string, value: string): void => {
    setValues((prev) => ({ ...prev, [itemId]: value }));
  };

  const onReorder = async (category: Category, ids: string[]): Promise<void> => {
    setItems((prev) => {
      const reorderedIds = new Set(ids);
      const inCategory = prev.filter((i) => i.category === category && !i.archivedAt);
      const others = prev.filter(
        (i) => i.category !== category || !!i.archivedAt || !reorderedIds.has(i.id),
      );
      const sorted: api.TrackingItem[] = ids
        .map((id, idx) => {
          const found = inCategory.find((i) => i.id === id);
          if (!found) return null;
          return { ...found, sortOrder: idx };
        })
        .filter((x): x is api.TrackingItem => x !== null);
      return [...others, ...sorted];
    });
    try {
      await api.reorderTrackingItems(accessToken, clientId, category, ids);
    } catch (cause) {
      setSubmitError(
        cause instanceof Error ? cause.message : t('checkIn.errors.saveFailed'),
      );
    }
  };

  const onArchiveItem = async (itemId: string): Promise<void> => {
    try {
      await api.updateTrackingItem(accessToken, clientId, itemId, { archived: true });
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, archivedAt: new Date().toISOString() } : i,
        ),
      );
    } catch (cause) {
      setSubmitError(
        cause instanceof Error ? cause.message : t('checkIn.errors.saveFailed'),
      );
    }
  };

  const onRenameItem = async (itemId: string, label: string): Promise<void> => {
    try {
      const updated = await api.updateTrackingItem(accessToken, clientId, itemId, {
        label,
      });
      setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
    } catch (cause) {
      setSubmitError(
        cause instanceof Error ? cause.message : t('checkIn.errors.saveFailed'),
      );
    }
  };

  // F-031 — replace virtue bindings for a tracker item. Optimistic
  // update on success, revert on failure. Catalog-only IDs are valid.
  const onSetVirtues = async (itemId: string, virtueIds: string[]): Promise<void> => {
    const prevSnapshot = items;
    const nextVirtues = virtueIds
      .map((vid) => catalog.find((v) => v.id === vid))
      .filter((v): v is api.CarpeDiemCatalogVirtue => v !== undefined)
      .map((v) => ({ id: v.id, name: v.name, axis: v.domain }));
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, virtues: nextVirtues } : i)),
    );
    try {
      await api.setTrackingItemVirtues(accessToken, clientId, itemId, virtueIds);
    } catch (cause) {
      setItems(prevSnapshot);
      setSubmitError(
        cause instanceof Error ? cause.message : t('checkIn.errors.saveFailed'),
      );
    }
  };

  // #15 — client sets a tracker item's weight factor (1 or 2; 3 is
  // coach-reserved). Optimistic, reverts on failure.
  const onSetWeight = async (itemId: string, weight: number): Promise<void> => {
    const prevSnapshot = items;
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, weight } : i)));
    try {
      const updated = await api.updateTrackingItem(accessToken, clientId, itemId, { weight });
      setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
    } catch (cause) {
      setItems(prevSnapshot);
      setSubmitError(
        cause instanceof Error ? cause.message : t('checkIn.errors.saveFailed'),
      );
    }
  };

  const activeItems = items.filter((i) => !i.archivedAt);
  const itemsByCategory: Record<Category, api.TrackingItem[]> = {
    am_bookend: activeItems
      .filter((i) => i.category === 'am_bookend')
      .sort((a, b) => a.sortOrder - b.sortOrder),
    pm_bookend: activeItems
      .filter((i) => i.category === 'pm_bookend')
      .sort((a, b) => a.sortOrder - b.sortOrder),
    anywhere: activeItems
      .filter((i) => i.category === 'anywhere')
      .sort((a, b) => a.sortOrder - b.sortOrder),
  };

  // Pretty-format the selected date for the header.
  const dateLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const prettyDate = new Date(`${date}T00:00:00.000Z`).toLocaleDateString(
    dateLocale,
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
  );

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7 pb-s10" data-testid="check-in">
      {/* NL-034 — WIN celebration banner. forceShow=true so the moment of
          saving always flashes, even if the once-per-day guard has
          already fired (e.g. dashboard opened first today). */}
      {celebrateWin ? (
        <WinCelebration
          clientId={clientId}
          date={date}
          winText={savedDailyWin}
          forceShow
        />
      ) : null}
      {/* batch #4 #8 — ONE Thing "done" celebration, fired on check-off. */}
      {celebrateOneThing ? (
        <OneThingCelebration
          oneThingText={savedOneThing}
          onDismiss={dismissOneThingCelebration}
        />
      ) : null}
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      {/* NL-006 — first-login banner when /check-in unlocks at Step 1. */}
      <StepEntryBanner stepNumber={1} variantKey="checkInUnlocked" />
      {/* NL-014 — first-login banner when Confidence Log unlocks at Step 3. */}
      <StepEntryBanner stepNumber={3} variantKey="confidenceLogOpen" />
      {/* NL-011 — Confidence Log card. Self-gates on currentStep >= 3;
          renders null otherwise so the layout doesn't shift mid-coaching. */}
      <ConfidenceLogCard clientId={clientId} currentStep={currentStep} />
      {/* batch #4 #8 — ONE Thing declaration card. Shows in the daily-card
          area (confidence-log manner) until the client declares today's ONE
          Thing; once declared it moves up to the checkable banner between the
          day nav and the KPIs (rendered below the header). */}
      {oneThingGateActive && (!oneThingDeclared || editingOneThing) ? (
        <section
          className="mb-s5 rounded-lg border border-flame/40 bg-bone-1 p-s5"
          data-testid="check-in-one-thing-card"
        >
          <header className="mb-s2">
            <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
              {t('checkIn.oneThing.eyebrow')}
            </p>
            <h2 className="font-display text-display-sm text-ink mt-s1">
              {t('checkIn.oneThing.title')}
            </h2>
            <p className="mt-s2 text-sm text-gray-2 italic">
              {t('checkIn.oneThing.subtitle')}
            </p>
          </header>
          <textarea
            value={oneThingText}
            onChange={(e) => setOneThingText(e.target.value)}
            placeholder={t('checkIn.oneThing.placeholder')}
            className="input-field min-h-20"
            data-testid="check-in-one-thing-input"
            rows={2}
            maxLength={500}
            aria-label={t('checkIn.oneThing.title')}
          />
          <div className="mt-s3 flex items-center justify-between gap-s3">
            <div className="text-xs" aria-live="polite">
              {oneThingError ? (
                <span role="alert" className="text-flame-deep" data-testid="check-in-one-thing-error">
                  {oneThingError}
                </span>
              ) : (
                <span aria-hidden="true">&nbsp;</span>
              )}
            </div>
            <div className="flex items-center gap-s2">
              {editingOneThing ? (
                <button
                  type="button"
                  onClick={() => {
                    setOneThingText(savedOneThing);
                    setEditingOneThing(false);
                    setOneThingError(null);
                  }}
                  className="btn-ghost text-sm"
                  data-testid="check-in-one-thing-cancel"
                >
                  {t('common.actions.cancel')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void saveOneThing()}
                disabled={oneThingSaving || !oneThingDirty}
                data-testid="check-in-one-thing-save"
                data-state={oneThingDirty ? 'dirty' : oneThingSavedPulse ? 'saved' : 'pristine'}
                className={
                  oneThingSavedPulse
                    ? 'btn-primary !bg-flame-deep text-sm transition-colors'
                    : oneThingDirty
                      ? 'btn-primary text-sm transition-colors'
                      : 'rounded-md px-s4 py-s2 text-sm font-semibold text-gray-2 bg-bone-3 cursor-default transition-colors'
                }
              >
                {oneThingSavedPulse
                  ? `✓ ${t('checkIn.oneThing.savedPulse')}`
                  : oneThingSaving
                    ? t('checkIn.oneThing.saving')
                    : t('checkIn.oneThing.save')}
              </button>
            </div>
          </div>
        </section>
      ) : null}
      {/* Gratitude is no longer a digital card — it's a locked AM-bookend
          tracking behavior ("Five things I'm thankful for (handwritten)")
          seeded by the Carpe Diem build, shown in the AM group below. */}
      {/* NL-019 — Weekly Focus Virtue picker. Self-gates on currentStep >= 5
          AND no virtue set yet for the current ISO week; otherwise renders a
          collapsed summary. */}
      <WeeklyFocusCard clientId={clientId} currentStep={currentStep} />
      {/* NL-042 / IMP-019 — Step 8+ nudge when the current ISO week has no
          weekly review yet. Self-gating; independent of tool assignment. */}
      <WeeklyReviewBanner clientId={clientId} currentStep={currentStep} />
      <header className="mb-s5">
        <h1 className="font-display text-display-lg text-ink">{t('checkIn.title')}</h1>
        <p className="mt-s2 text-gray-2">{t('checkIn.subtitle')}</p>
        {/* F-016: date navigation — ← previous day · pretty date · next day → */}
        <nav
          className="mt-s4 flex items-center justify-center gap-s4 rounded-md border border-bone-3 bg-bone-2 p-s3"
          data-testid="check-in-date-nav"
        >
          <button
            type="button"
            disabled={!canGoBack}
            onClick={() => setDate(shiftIso(date, -1))}
            className="px-s3 py-s1 rounded text-flame-ember hover:text-flame disabled:opacity-30 disabled:cursor-not-allowed font-mono"
            data-testid="check-in-prev-day"
            aria-label={t('checkIn.date.prev')}
          >
            ◀
          </button>
          <span
            className="font-display text-base text-ink min-w-[16ch] text-center"
            data-testid="check-in-current-date"
          >
            {isToday ? `${t('checkIn.date.today')} · ${prettyDate}` : prettyDate}
          </span>
          <button
            type="button"
            disabled={!canGoForward}
            onClick={() => setDate(shiftIso(date, 1))}
            className="px-s3 py-s1 rounded text-flame-ember hover:text-flame disabled:opacity-30 disabled:cursor-not-allowed font-mono"
            data-testid="check-in-next-day"
            aria-label={t('checkIn.date.next')}
          >
            ▶
          </button>
        </nav>
      </header>

      {/* batch #4 #8 — ONE Thing checkable banner. Once today's ONE Thing is
          declared it lives here, between the day nav and the KPIs. Checking
          it off fires the celebration. */}
      {oneThingGateActive && oneThingDeclared && !editingOneThing ? (
        <article
          className={
            'mb-s5 flex items-center gap-s3 rounded-lg border-2 p-s4 transition-colors ' +
            (oneThingDone ? 'border-flame/40 bg-flame/[0.04]' : 'border-flame bg-flame/[0.07]')
          }
          data-testid="check-in-one-thing-banner"
          data-done={oneThingDone ? 'true' : 'false'}
        >
          <input
            type="checkbox"
            checked={oneThingDone}
            onChange={(e) => void toggleOneThingDone(e.target.checked)}
            className="h-6 w-6 shrink-0 cursor-pointer accent-flame"
            data-testid="check-in-one-thing-checkbox"
            aria-label={t('checkIn.oneThing.checkboxLabel')}
          />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wide text-flame-ember">
              {t('checkIn.oneThing.bannerEyebrow')}
            </p>
            <p
              className={
                'font-display text-base ' +
                (oneThingDone ? 'text-gray-1 line-through decoration-flame' : 'text-ink')
              }
              data-testid="check-in-one-thing-banner-text"
            >
              {savedOneThing}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setOneThingText(savedOneThing);
              setEditingOneThing(true);
            }}
            className="shrink-0 text-sm text-gray-1 hover:text-flame-ember"
            aria-label={t('checkIn.oneThing.edit')}
            title={t('checkIn.oneThing.edit')}
            data-testid="check-in-one-thing-edit"
          >
            ✎
          </button>
        </article>
      ) : null}

      {/* ── KPIs ── */}
      <KpiSection kpis={kpis} savedBaselines={savedBaselines} onChange={setKpis} />

      {/* NL-033 — Daily WIN entry. Visible at Step 6+ only. Locks the
          tracker UI below until ≥3 chars entered (or a saved value
          loaded from a prior day). */}
      {winGateActive ? (
        <article
          className="mt-s5 rounded-lg border border-flame/40 bg-flame/[0.04] p-s5"
          data-testid="check-in-daily-win"
        >
          <header className="mb-s3">
            <h2 className="font-display text-display-sm text-ink">
              {t('checkIn.dailyWin.title')}
            </h2>
            <p className="mt-s2 text-sm text-gray-2">
              {t('checkIn.dailyWin.subtitle')}
            </p>
          </header>
          <textarea
            value={dailyWin}
            onChange={(e) => setDailyWin(e.target.value)}
            placeholder={t('checkIn.dailyWin.placeholder')}
            rows={2}
            maxLength={500}
            className="w-full rounded-md border border-bone-3 bg-white p-s3 font-display text-base text-ink focus:outline-none focus:border-flame focus:ring-1 focus:ring-flame"
            data-testid="check-in-daily-win-input"
            aria-label={t('checkIn.dailyWin.title')}
          />
          {!trackerUnlocked ? (
            <p className="mt-s2 text-xs text-flame-ember italic" data-testid="check-in-daily-win-gate">
              {t('checkIn.dailyWin.gate')}
            </p>
          ) : null}
        </article>
      ) : null}

      {/* ── Hero Tracker ── */}
      <article
        className={
          'mt-s5 rounded-lg border border-bone-3 bg-bone-2 p-s5 transition-opacity ' +
          (trackerUnlocked ? '' : 'opacity-40 pointer-events-none select-none')
        }
        aria-disabled={!trackerUnlocked}
        data-testid="check-in-tracker"
        data-tracker-unlocked={trackerUnlocked ? 'true' : 'false'}
      >
        <header className="mb-s4">
          <h2 className="font-display text-display-sm text-ink">
            {t('checkIn.tracker.title')}
          </h2>
          <p className="mt-s2 text-sm text-gray-2">{t('checkIn.tracker.subtitle')}</p>
        </header>

        {/* IMP-011 — with zero items (Step 1-2, before the Carpe Diem
            protocol is built) skip the three empty AM/PM/Anywhere groups
            and explain WHY the tracker is empty instead of looking broken.
            The bookend system items seed on protocol build by design
            (NL-003: lazy seed). */}
        {activeItems.length === 0 ? (
          <p className="text-sm text-gray-2 italic" data-testid="check-in-tracker-empty">
            {t('checkIn.tracker.empty')}
          </p>
        ) : (
          CATEGORIES.map((category) => (
            <TrackerGroup
              key={category}
              category={category}
              items={itemsByCategory[category]}
              values={values}
              catalog={catalog}
              onValueChange={onValueChange}
              onReorder={(ids) => void onReorder(category, ids)}
              onArchive={(id) => {
                const it = items.find((i) => i.id === id);
                setArchiveConfirm({ id, label: it?.label ?? '' });
              }}
              onRename={(id, label) => void onRenameItem(id, label)}
              onSetVirtues={(id, vids) => void onSetVirtues(id, vids)}
              onSetWeight={(id, weight) => void onSetWeight(id, weight)}
            />
          ))
        )}

        <AddItemForm onAdd={(input) => void onAddItem(input)} />
      </article>

      {/* ── Errors ── */}
      {submitError && (
        <p className="mt-s4 form-error" role="alert" data-testid="check-in-error">
          {submitError}
        </p>
      )}

      {/* ── Save button (sticky bottom) ── */}
      <div className="mt-s6 sticky bottom-s2 z-10 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          data-testid="check-in-save"
          data-state={justSaved ? 'just-saved' : dirty ? 'dirty' : 'pristine'}
          className={
            justSaved
              ? 'btn-primary !bg-flame-deep transition-colors'
              : dirty
                ? 'btn-primary transition-colors'
                : 'rounded-md px-s5 py-s3 font-semibold text-gray-2 bg-bone-3 cursor-default transition-colors'
          }
          aria-live="polite"
        >
          {justSaved
            ? `✓ ${t('checkIn.save.saved')}`
            : submitting
              ? t('checkIn.save.saving')
              : dirty
                ? t('checkIn.save.cta')
                : t('checkIn.save.idle')}
        </button>
      </div>

      {/* #12 — confirm before persisting untouched default KPIs. */}
      {kpiConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="kpi-confirm-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-s4"
          data-testid="kpi-confirm"
        >
          <article className="bg-bone rounded-lg shadow-xl border border-bone-3 max-w-md w-full p-s6">
            <h2
              id="kpi-confirm-title"
              className="font-display text-display-md text-ink"
              data-testid="kpi-confirm-title"
            >
              {t('checkIn.kpiConfirm.title')}
            </h2>
            <p className="mt-s3 text-gray-2" data-testid="kpi-confirm-body">
              {t('checkIn.kpiConfirm.body', {
                energy: DEFAULT_KPIS.energy,
                work: DEFAULT_KPIS.work,
                love: DEFAULT_KPIS.love,
              })}
            </p>
            <div className="mt-s5 flex flex-wrap gap-s3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setKpiConfirmOpen(false);
                  void doSave(false);
                }}
                className="btn-ghost text-sm"
                data-testid="kpi-confirm-skip"
              >
                {t('checkIn.kpiConfirm.skip')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setKpiConfirmOpen(false);
                  void doSave(true);
                }}
                className="btn-primary text-sm"
                data-testid="kpi-confirm-save"
              >
                {t('checkIn.kpiConfirm.save')}
              </button>
            </div>
          </article>
        </div>
      )}

      {/* #3 — confirm before removing a tracker item (soft-archive). */}
      {archiveConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="archive-confirm-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-s4"
          data-testid="archive-confirm"
        >
          <article className="bg-bone rounded-lg shadow-xl border border-bone-3 max-w-md w-full p-s6">
            <h2
              id="archive-confirm-title"
              className="font-display text-display-md text-ink"
              data-testid="archive-confirm-title"
            >
              {t('checkIn.archiveConfirm.title')}
            </h2>
            <p className="mt-s3 text-gray-2" data-testid="archive-confirm-body">
              {t('checkIn.archiveConfirm.body', {
                label: archiveConfirm.label || t('checkIn.archiveConfirm.thisItem'),
              })}
            </p>
            <div className="mt-s5 flex flex-wrap gap-s3 justify-end">
              <button
                type="button"
                onClick={() => setArchiveConfirm(null)}
                className="btn-ghost text-sm"
                data-testid="archive-confirm-cancel"
              >
                {t('checkIn.archiveConfirm.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = archiveConfirm.id;
                  setArchiveConfirm(null);
                  void onArchiveItem(id);
                }}
                className="btn-primary !bg-flame-ember text-sm"
                data-testid="archive-confirm-delete"
              >
                {t('checkIn.archiveConfirm.confirm')}
              </button>
            </div>
          </article>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────  KPI section  ───────────────────────────── */

function KpiSection({
  kpis,
  savedBaselines,
  onChange,
}: {
  kpis: Kpis;
  savedBaselines: { energy: number | null; work: number | null; love: number | null };
  onChange: (next: Kpis) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      className="rounded-lg border border-bone-3 bg-bone-2 p-s5"
      data-testid="check-in-kpis"
    >
      <header className="mb-s4">
        <h2 className="font-display text-display-sm text-ink">
          {t('checkIn.kpis.title')}
        </h2>
        <p className="mt-s2 text-sm text-gray-2">{t('checkIn.kpis.subtitle')}</p>
      </header>
      <div className="space-y-s4">
        <KpiSlider
          labelKey="checkIn.kpis.energy"
          value={kpis.energy}
          state={kpiState(kpis.energy, savedBaselines.energy, DEFAULT_KPIS.energy)}
          onChange={(v) => onChange({ ...kpis, energy: v })}
          testId="kpi-energy"
        />
        <KpiSlider
          labelKey="checkIn.kpis.work"
          value={kpis.work}
          state={kpiState(kpis.work, savedBaselines.work, DEFAULT_KPIS.work)}
          onChange={(v) => onChange({ ...kpis, work: v })}
          testId="kpi-work"
        />
        <KpiSlider
          labelKey="checkIn.kpis.love"
          value={kpis.love}
          state={kpiState(kpis.love, savedBaselines.love, DEFAULT_KPIS.love)}
          onChange={(v) => onChange({ ...kpis, love: v })}
          testId="kpi-love"
        />
      </div>
    </article>
  );
}

type KpiState = 'unset' | 'dirty' | 'saved';

/**
 * F-032 — slider tri-state. The colour is the at-a-glance signal:
 *   - unset  (black): no saved value for this day AND the slider is
 *                     still at the default — user has not engaged yet
 *   - dirty  (orange/flame): user dragged → differs from saved baseline
 *                            (or differs from default when no save yet)
 *   - saved  (light grey): current value matches the saved baseline
 *                          — settled, no attention needed (per design-
 *                          system convention for confirmed/idle state)
 */
function kpiState(current: number, baseline: number | null, defaultValue: number): KpiState {
  if (baseline === null) {
    return current === defaultValue ? 'unset' : 'dirty';
  }
  return current === baseline ? 'saved' : 'dirty';
}

// Tailwind needs the literal class names somewhere in the source tree
// so the JIT keeps them in the bundle. Listed here so codepaths that
// derive them stay valid: accent-ink accent-flame accent-gray-1
const ACCENT_BY_STATE: Record<KpiState, string> = {
  unset: 'accent-ink',
  dirty: 'accent-flame',
  saved: 'accent-gray-1',
};

function KpiSlider({
  labelKey,
  value,
  state,
  onChange,
  testId,
}: {
  labelKey: string;
  value: number;
  state: KpiState;
  onChange: (v: number) => void;
  testId: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-bone-3 bg-bone p-s4">
      <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
        {t(labelKey)}
      </p>
      <div className="mt-s2 flex items-center gap-s3">
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={value}
          onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
          className={`flex-1 ${ACCENT_BY_STATE[state]}`}
          data-testid={testId}
          data-state={state}
          aria-label={t(labelKey)}
        />
        <span className="font-display text-display-sm text-ink min-w-[2ch] text-right">
          {value}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────  Hero Tracker group  ───────────────────────────── */

function TrackerGroup({
  category,
  items,
  values,
  catalog,
  onValueChange,
  onReorder,
  onArchive,
  onRename,
  onSetVirtues,
  onSetWeight,
}: {
  category: Category;
  items: api.TrackingItem[];
  values: Record<string, string>;
  catalog: api.CarpeDiemCatalogVirtue[];
  onValueChange: (itemId: string, value: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onArchive: (itemId: string) => void;
  onRename: (itemId: string, label: string) => void;
  onSetVirtues: (itemId: string, virtueIds: string[]) => void;
  onSetWeight: (itemId: string, weight: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const move = (idx: number, direction: -1 | 1): void => {
    const next = [...items];
    const swap = idx + direction;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap]!, next[idx]!];
    onReorder(next.map((i) => i.id));
  };
  return (
    <section className="mt-s4 first:mt-0" data-testid={`tracker-group-${category}`}>
      <h3 className="font-mono text-xs uppercase tracking-wide text-flame-ember mb-s2">
        {t(`checkIn.tracker.category.${category}`)}
        <span className="ml-s2 text-gray-1">({items.length})</span>
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-2 italic ml-s2">
          {t('checkIn.tracker.groupEmpty')}
        </p>
      ) : (
        <ul className="space-y-s2">
          {items.map((item, idx) => (
            <TrackerRow
              key={item.id}
              item={item}
              value={values[item.id] ?? item.defaultValue}
              catalog={catalog}
              onValueChange={(v) => onValueChange(item.id, v)}
              onUp={idx === 0 ? null : () => move(idx, -1)}
              onDown={idx === items.length - 1 ? null : () => move(idx, 1)}
              onArchive={() => onArchive(item.id)}
              onRename={(label) => onRename(item.id, label)}
              onSetVirtues={(vids) => onSetVirtues(item.id, vids)}
              onSetWeight={(weight) => onSetWeight(item.id, weight)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TrackerRow({
  item,
  value,
  catalog,
  onValueChange,
  onUp,
  onDown,
  onArchive,
  onRename,
  onSetVirtues,
  onSetWeight,
}: {
  item: api.TrackingItem;
  value: string;
  catalog: api.CarpeDiemCatalogVirtue[];
  onValueChange: (v: string) => void;
  onUp: (() => void) | null;
  onDown: (() => void) | null;
  onArchive: () => void;
  onRename: (label: string) => void;
  onSetVirtues: (virtueIds: string[]) => void;
  onSetWeight: (weight: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [draftLabel, setDraftLabel] = useState(item.label);
  const [tagging, setTagging] = useState(false);
  const isDone =
    item.itemType === 'yes_no'
      ? value === 'true'
      : Number.parseFloat(value) > 0;
  // F-030 follow-up — system-managed items (auto-created by the
  // Carpe Diem build) get a flame-glow tint + lock icon + no rename /
  // archive affordances. Clients can't remove them; only the coach can.
  const isSystemManaged = item.createdBy === 'system';
  // F-031 — catalog grouped by axis for the picker.
  const catalogByAxis: Record<api.VirtueAxis, api.CarpeDiemCatalogVirtue[]> = {
    energy: catalog.filter((v) => v.domain === 'energy').sort((a, b) => a.priority - b.priority),
    work: catalog.filter((v) => v.domain === 'work').sort((a, b) => a.priority - b.priority),
    love: catalog.filter((v) => v.domain === 'love').sort((a, b) => a.priority - b.priority),
  };
  const taggedIds = new Set(item.virtues.map((v) => v.id));
  // Unique axes the item is currently bound to — what we render as
  // chips next to the label.
  const axesBound: api.VirtueAxis[] = (['energy', 'work', 'love'] as const)
    .filter((axis) => item.virtues.some((v) => v.axis === axis));
  // #15 — weight factor. Clients set 1 or 2; 3 is coach-reserved (shown
  // locked). System-managed items aren't client-editable at all.
  const effectiveWeight = item.weight ?? 1;
  const coachLockedWeight = item.weight === 3;
  return (
    <li
      className={`rounded-md py-s2 px-s3 ${
        isSystemManaged
          ? 'border border-flame-deep/40 bg-flame-glow/20'
          : 'border border-bone-3 bg-bone'
      }`}
      data-testid={`tracker-row-${item.id}`}
    >
      <div className="flex items-center gap-s2">
      {/* Reorder arrows — hidden for system items (their order is fixed). */}
      {!isSystemManaged && (
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => onUp?.()}
            disabled={!onUp}
            className="text-gray-1 hover:text-flame-ember disabled:opacity-30"
            aria-label={t('checkIn.tracker.moveUp')}
            data-testid={`tracker-up-${item.id}`}
          >
            ▲
          </button>
          <button
            type="button"
            onClick={() => onDown?.()}
            disabled={!onDown}
            className="text-gray-1 hover:text-flame-ember disabled:opacity-30"
            aria-label={t('checkIn.tracker.moveDown')}
            data-testid={`tracker-down-${item.id}`}
          >
            ▼
          </button>
        </div>
      )}
      {isSystemManaged && (
        <span
          className="text-flame-deep"
          aria-label={t('checkIn.tracker.systemManaged')}
          title={t('checkIn.tracker.systemManaged')}
          data-testid={`tracker-locked-${item.id}`}
        >
          🔒
        </span>
      )}

      {/* Yes/No or number control */}
      {item.itemType === 'yes_no' ? (
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onValueChange(e.target.checked ? 'true' : 'false')}
          className="h-5 w-5 accent-flame"
          data-testid={`tracker-yesno-${item.id}`}
          aria-label={item.label}
        />
      ) : (
        <input
          type="number"
          min={0}
          max={9999}
          step={1}
          value={value === '' ? '' : Number.parseInt(value, 10) || 0}
          onChange={(e) => onValueChange(String(Math.max(0, Number.parseInt(e.target.value, 10) || 0)))}
          className="w-20 rounded border border-bone-3 bg-bone-2 px-s2 py-s1 text-right font-mono"
          data-testid={`tracker-number-${item.id}`}
          aria-label={item.label}
        />
      )}

      {/* Label (or inline rename input). System items: render as plain
          text — no inline rename, no archive button (client can't remove). */}
      {isSystemManaged ? (
        <span
          className={`flex-1 text-left ${isDone ? 'text-ink line-through decoration-flame' : 'text-ink font-medium'}`}
          data-testid={`tracker-label-${item.id}`}
        >
          {item.label}
        </span>
      ) : renaming ? (
        <input
          type="text"
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          onBlur={() => {
            if (draftLabel.trim() && draftLabel.trim() !== item.label) {
              onRename(draftLabel.trim());
            }
            setRenaming(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDraftLabel(item.label);
              setRenaming(false);
            }
          }}
          autoFocus
          className="flex-1 rounded border border-flame bg-bone-2 px-s2 py-s1"
          data-testid={`tracker-rename-${item.id}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraftLabel(item.label);
            setRenaming(true);
          }}
          className={`flex-1 text-left ${isDone ? 'text-ink line-through decoration-flame' : 'text-ink'}`}
          data-testid={`tracker-label-${item.id}`}
        >
          {item.label}
        </button>
      )}

      {/* Axis chips — read-only summary of what virtues this item
          serves. Hidden when the catalog is empty (coach hasn't built
          the Carpe Diem yet — there's nothing to tag). */}
      {catalog.length > 0 && axesBound.length > 0 && (
        <div
          className="flex items-center gap-1"
          data-testid={`tracker-axes-${item.id}`}
        >
          {axesBound.map((axis) => (
            <AxisIcon
              key={axis}
              axis={axis}
              size={14}
              ariaLabel={t(`common.axis.${axis}`)}
            />
          ))}
        </div>
      )}

      {/* #15 — weight factor (1× / 2×). 3× is coach-reserved → shown locked.
          System-managed items aren't client-editable. */}
      {!isSystemManaged &&
        (coachLockedWeight ? (
          <span
            className="rounded bg-bone-3 px-s2 py-0.5 font-mono text-xs text-gray-2"
            title={t('checkIn.tracker.weight.coachLocked')}
            data-testid={`tracker-weight-locked-${item.id}`}
          >
            3×
          </span>
        ) : (
          <div
            className="flex overflow-hidden rounded border border-bone-3"
            role="group"
            aria-label={t('checkIn.tracker.weight.label')}
            data-testid={`tracker-weight-${item.id}`}
          >
            {[1, 2].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => onSetWeight(w)}
                aria-pressed={effectiveWeight === w}
                title={t('checkIn.tracker.weight.hint')}
                data-testid={`tracker-weight-${w}-${item.id}`}
                className={`px-s2 py-0.5 font-mono text-xs transition-colors ${
                  effectiveWeight === w
                    ? 'bg-flame text-bone'
                    : 'bg-bone-2 text-gray-2 hover:text-ink'
                }`}
              >
                {w}×
              </button>
            ))}
          </div>
        ))}

      {/* Tag button — opens the axis picker. Hidden when the catalog
          is empty + for system-managed items (those tags are fixed). */}
      {!isSystemManaged && catalog.length > 0 && (
        <button
          type="button"
          onClick={() => setTagging((v) => !v)}
          className={`text-sm font-medium ${
            tagging ? 'text-flame' : 'text-gray-1 hover:text-flame-ember'
          }`}
          aria-label={t('checkIn.tracker.tagAxes')}
          aria-expanded={tagging}
          data-testid={`tracker-tag-${item.id}`}
        >
          {tagging ? '✓' : '⊕'}
        </button>
      )}

      {/* Archive — system items are not deletable by the client. */}
      {!isSystemManaged && (
        <button
          type="button"
          onClick={() => onArchive()}
          className="text-sm text-gray-1 hover:text-flame-ember"
          aria-label={t('checkIn.tracker.archive')}
          data-testid={`tracker-archive-${item.id}`}
        >
          ✕
        </button>
      )}
      </div>

      {/* F-031 — expanded axis-tag picker. Multi-select; the same item
          can serve multiple axes' virtues. Each chip is a catalog
          virtue. Empty axes hidden. */}
      {tagging && catalog.length > 0 && (
        <div
          className="mt-s3 pt-s3 border-t border-bone-3 space-y-s2"
          data-testid={`tracker-tag-picker-${item.id}`}
        >
          <p className="text-xs text-gray-2">{t('checkIn.tracker.tagAxesHint')}</p>
          {(['energy', 'work', 'love'] as const).map((axis) => {
            const virtues = catalogByAxis[axis];
            if (virtues.length === 0) return null;
            return (
              <div key={axis} className="flex flex-wrap items-center gap-1">
                <AxisIcon axis={axis} size={13} />
                <span className="text-xs font-mono uppercase tracking-wide text-gray-2 mr-s2">
                  {t(`common.axis.${axis}`)}
                </span>
                {virtues.map((v) => (
                  <AxisChip
                    key={v.id}
                    axis={axis}
                    selected={taggedIds.has(v.id)}
                    label={v.name}
                    testId={`tracker-tag-chip-${item.id}-${v.id}`}
                    onClick={() => {
                      const next = taggedIds.has(v.id)
                        ? [...taggedIds].filter((id) => id !== v.id)
                        : [...taggedIds, v.id];
                      onSetVirtues(next);
                    }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}

/* ─────────────────────────────  Add item form  ───────────────────────────── */

function AddItemForm({
  onAdd,
}: {
  onAdd: (input: { label: string; category: Category; itemType: ItemType }) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<Category>('anywhere');
  const [itemType, setItemType] = useState<ItemType>('yes_no');
  return (
    <div className="mt-s4 pt-s4 border-t border-bone-3">
      {open ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!label.trim()) return;
            onAdd({ label: label.trim(), category, itemType });
            setLabel('');
            setCategory('anywhere');
            setItemType('yes_no');
            setOpen(false);
          }}
          className="space-y-s3"
        >
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('checkIn.tracker.add.placeholder')}
            maxLength={120}
            className="w-full rounded border border-bone-3 bg-bone-2 px-s3 py-s2"
            autoFocus
            data-testid="tracker-add-label"
          />
          <div className="flex flex-wrap gap-s2 text-sm">
            <span className="text-gray-2 self-center mr-s2">
              {t('checkIn.tracker.add.category')}
            </span>
            {(['am_bookend', 'pm_bookend', 'anywhere'] as const).map((c) => (
              <label key={c} className="cursor-pointer">
                <input
                  type="radio"
                  name="cat"
                  value={c}
                  checked={category === c}
                  onChange={() => setCategory(c)}
                  className="sr-only peer"
                />
                <span
                  className="px-s3 py-s1 rounded-full border border-bone-3 peer-checked:border-flame peer-checked:bg-flame peer-checked:text-bone text-ink"
                  data-testid={`tracker-add-cat-${c}`}
                >
                  {t(`checkIn.tracker.category.${c}`)}
                </span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-s2 text-sm">
            <span className="text-gray-2 self-center mr-s2">
              {t('checkIn.tracker.add.type')}
            </span>
            {(['yes_no', 'number'] as const).map((tt) => (
              <label key={tt} className="cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  value={tt}
                  checked={itemType === tt}
                  onChange={() => setItemType(tt)}
                  className="sr-only peer"
                />
                <span
                  className="px-s3 py-s1 rounded-full border border-bone-3 peer-checked:border-flame peer-checked:bg-flame peer-checked:text-bone text-ink"
                  data-testid={`tracker-add-type-${tt}`}
                >
                  {t(`checkIn.tracker.add.type.${tt}`)}
                </span>
              </label>
            ))}
          </div>
          <div className="flex gap-s2">
            <button
              type="submit"
              disabled={!label.trim()}
              className="btn-primary"
              data-testid="tracker-add-submit"
            >
              {t('checkIn.tracker.add.cta')}
            </button>
            <button
              type="button"
              onClick={() => {
                setLabel('');
                setOpen(false);
              }}
              className="rounded-md px-s4 py-s2 text-gray-2 hover:text-ink"
              data-testid="tracker-add-cancel"
            >
              {t('common.actions.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-flame-ember hover:text-flame font-medium"
          data-testid="tracker-add-open"
        >
          + {t('checkIn.tracker.add.open')}
        </button>
      )}
    </div>
  );
}
